import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import {
  Search as SearchIcon, Sparkles, MapPin, Building2, X, ShieldCheck, Crown,
  ArrowRight, Loader2, Briefcase, Calendar, Filter, ChevronDown, Bell,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";

function FacetGroup({ title, items, selected, onToggle, max = 8 }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, max);
  return (
    <div className="border-t border-white/10 pt-5 first:border-t-0 first:pt-0">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">{title}</div>
      <ul className="space-y-1.5">
        {visible.map((it) => {
          const active = selected.has(it.value);
          return (
            <li key={it.value}>
              <button
                type="button"
                onClick={() => onToggle(it.value)}
                className={`w-full text-start flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-sm transition-colors ${
                  active ? "bg-white text-[#1a1a1c] font-bold" : "text-white/75 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`facet-${title}-${it.value}`}
              >
                <span className="truncate">
                  {active && <X className="inline w-3 h-3 -mt-0.5 me-1" />}
                  {it.value}
                </span>
                <span className={`font-mono text-[11px] tabular-nums shrink-0 ${active ? "text-[#1a1a1c]/70" : "text-white/45"}`}>
                  {it.count}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {items.length > max && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-xs font-medium text-white/55 hover:text-white"
        >
          {expanded ? "Show less ↑" : `Show all ${items.length} →`}
        </button>
      )}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (diffDays <= 0) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function Search() {
  const { t } = useI18n();

  // Search inputs
  const [what, setWhat] = useState("");
  const [where, setWhere] = useState("");

  // Applied filters (drive the API call)
  const [appliedRole, setAppliedRole] = useState("");
  const [appliedLocation, setAppliedLocation] = useState("");  // free-text "where"
  const [selectedCities, setSelectedCities] = useState(new Set());
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());

  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState({ total: 0, cities: [], categories: [], companies: [] });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Per-card expanded description state — controlled separately from selection
  // so opening details never accidentally toggles the bulk-apply checkbox.
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Bulk apply
  const [selected, setSelected] = useState(new Set());
  const [consentOpen, setConsentOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [applying, setApplying] = useState(false);
  const [quotaModal, setQuotaModal] = useState(null);
  const [sender, setSender] = useState("maqam");          // "gmail" | "maqam"
  const [gmailConnected, setGmailConnected] = useState(false);

  // Check Gmail connection on mount
  useEffect(() => {
    api.get("/oauth/gmail/status")
      .then((r) => {
        const connected = !!r.data?.connected;
        setGmailConnected(connected);
        if (connected) setSender("gmail");   // prefer Gmail when available
      })
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const body = {
      role: appliedRole,
      location: appliedLocation,
      cities: [...selectedCities],
      categories: [...selectedCats],
      companies: [...selectedCompanies],
      limit: 80,
    };
    try {
      const [r1, r2] = await Promise.all([
        api.post("/jobs/search", body),
        api.post("/jobs/facets", body),
      ]);
      setResults(r1.data || []);
      setFacets(r2.data || { total: 0, cities: [], categories: [], companies: [] });
    } catch (e) {
      toast.error("Could not load jobs");
    } finally {
      setLoading(false);
    }
  }, [appliedRole, appliedLocation, selectedCities, selectedCats, selectedCompanies]);

  // Initial load + reactive load on filter changes
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSearch = (e) => {
    e?.preventDefault();
    setAppliedRole(what.trim());
    // Free-text "where" is treated as a fuzzy location query
    // (matched case-insensitively against city, with country-name aliases
    // expanded server-side). It does NOT replace explicit city checkboxes —
    // toggling a city in the filter panel still works independently.
    setAppliedLocation(where.trim());
  };

  const saveSearch = async () => {
    if (!appliedRole && !appliedLocation && selectedCities.size === 0 && selectedCats.size === 0 && selectedCompanies.size === 0) {
      toast.error("Pick a keyword or filter first, then save.");
      return;
    }
    try {
      await api.post("/alerts", {
        role: appliedRole,
        location: appliedLocation,
        cities: [...selectedCities],
        categories: [...selectedCats],
        companies: [...selectedCompanies],
      });
      toast.success("Search saved — we'll email you when new jobs match.");
    } catch {
      toast.error("Could not save this search");
    }
  };

  const toggleSet = (setter, set, value) => {
    const n = new Set(set);
    if (n.has(value)) n.delete(value);
    else n.add(value);
    setter(n);
  };

  const clearAll = () => {
    setWhat(""); setWhere("");
    setAppliedRole("");
    setSelectedCities(new Set());
    setSelectedCats(new Set());
    setSelectedCompanies(new Set());
  };

  const activeFilterCount =
    selectedCities.size + selectedCats.size + selectedCompanies.size + (appliedRole ? 1 : 0);

  const toggleSelect = (id) => {
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const allSelected = useMemo(
    () => results.length > 0 && selected.size === results.length,
    [selected, results]
  );
  const selectAll = () =>
    setSelected(allSelected ? new Set() : new Set(results.map((r) => r.id)));

  const doApply = async () => {
    if (!consent) { toast.error("You must consent to continue"); return; }
    setApplying(true);
    try {
      const r = await api.post("/applications/bulk", { job_ids: [...selected], consent: true, sender });
      const delivered = r.data.delivered || 0; const drafted = r.data.drafted || 0;
      const via = sender === "gmail" ? "your Gmail" : "Maqam";
      let msg = delivered > 0 && drafted > 0
        ? `${delivered} sent via ${via} · ${drafted} drafts (no employer email)`
        : delivered > 0 ? `${delivered} applications sent via ${via} with CV + documents attached`
        : `${r.data.created} drafts created — open each to "Apply on source"`;
      if (r.data.truncated > 0) msg += ` — ${r.data.truncated} skipped (quota)`;
      toast.success(msg);
      setConsentOpen(false); setConsent(false); setSelected(new Set());
      if (r.data.allowance?.remaining === 0) setQuotaModal(r.data.allowance);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "quota_exceeded") {
        setConsentOpen(false); setConsent(false); setQuotaModal(detail.allowance);
      } else toast.error(typeof detail === "string" ? detail : "Bulk apply failed");
    } finally { setApplying(false); }
  };

  const Sidebar = (
    <aside className="space-y-6">
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-base">Filters</div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs font-medium text-white/55 hover:text-white"
              data-testid="clear-filters"
            >
              Clear all ({activeFilterCount})
            </button>
          )}
        </div>

        {appliedRole && (
          <div className="mb-5 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-[#1a1a1c] rounded-full text-xs font-bold">
              "{appliedRole}"
              <button onClick={() => { setAppliedRole(""); setWhat(""); }}>
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}

        <div className="space-y-5">
          <FacetGroup
            title="Industry"
            items={facets.categories}
            selected={selectedCats}
            onToggle={(v) => toggleSet(setSelectedCats, selectedCats, v)}
          />
          <FacetGroup
            title="Location"
            items={facets.cities}
            selected={selectedCities}
            onToggle={(v) => toggleSet(setSelectedCities, selectedCities, v)}
          />
          <FacetGroup
            title="Company"
            items={facets.companies}
            selected={selectedCompanies}
            onToggle={(v) => toggleSet(setSelectedCompanies, selectedCompanies, v)}
            max={6}
          />
        </div>
      </div>
    </aside>
  );

  return (
    <div className="space-y-6 text-white" data-testid="search-page">
      {/* Top header + search bar */}
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-2">/ Search engine</div>
        <h1 className="font-display font-normal text-4xl md:text-5xl tracking-tight text-white">
          {loading && results.length === 0 ? "Loading jobs…" : (
            <>
              <span className="num-ticker font-bold">{facets.total.toLocaleString()}</span> Gulf jobs ready to apply
            </>
          )}
        </h1>
      </div>

      <form
        onSubmit={onSearch}
        className="bg-white/[0.04] border border-white/10 rounded-2xl p-3 md:p-4 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3"
        data-testid="search-form"
      >
        <div className="md:col-span-5 flex items-center gap-2 px-3 bg-white/[0.04] border border-white/10 rounded-xl">
          <SearchIcon className="w-4 h-4 text-white/50 shrink-0" />
          <input
            type="text"
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            placeholder="What? e.g. nurse, engineer, marketing…"
            className="flex-1 bg-transparent py-3 outline-none text-white placeholder-white/40 text-base"
            data-testid="search-what"
          />
        </div>
        <div className="md:col-span-4 flex items-center gap-2 px-3 bg-white/[0.04] border border-white/10 rounded-xl">
          <MapPin className="w-4 h-4 text-white/50 shrink-0" />
          <input
            type="text"
            value={where}
            onChange={(e) => setWhere(e.target.value)}
            placeholder="Where? e.g. Riyadh, Dubai…"
            className="flex-1 bg-transparent py-3 outline-none text-white placeholder-white/40 text-base"
            data-testid="search-where"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="md:col-span-3 bg-white text-[#1a1a1c] hover:bg-white/90 font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          data-testid="search-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
          Find jobs
        </button>
      </form>

      {/* Mobile filter toggle */}
      <button
        type="button"
        onClick={() => setFiltersOpen((v) => !v)}
        className="md:hidden inline-flex items-center gap-2 px-4 py-2 border border-white/15 rounded-full text-sm font-medium"
        data-testid="mobile-filters-toggle"
      >
        <Filter className="w-4 h-4" />
        Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Layout: sidebar + results */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className={`md:col-span-3 ${filtersOpen ? "block" : "hidden md:block"}`}>{Sidebar}</div>

        <div className="md:col-span-9 space-y-3">
          {/* Action bar */}
          {!loading && results.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-y border-white/10 py-3">
              <div className="flex flex-wrap items-center gap-3 text-sm text-white/60">
                <span>
                  Showing <span className="font-bold text-white num-ticker">{results.length}</span>
                  {" of "}
                  <span className="font-bold text-white num-ticker">{facets.total.toLocaleString()}</span>
                </span>
                <span className="text-white/30">·</span>
                <button onClick={selectAll} className="font-mono text-xs uppercase tracking-widest text-white/80 hover:text-white" data-testid="select-all">
                  {allSelected ? "Deselect all" : "Select all"}
                </button>
                <span className="text-white/30">·</span>
                <button
                  type="button"
                  onClick={saveSearch}
                  className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-white/80 hover:text-white"
                  data-testid="save-search-btn"
                  title="Save this search and get email alerts when new jobs match"
                >
                  <Bell className="w-3.5 h-3.5" /> Save & alert me
                </button>
              </div>
              <button
                onClick={() => selected.size > 0 && setConsentOpen(true)}
                disabled={selected.size === 0}
                className="bg-white text-[#1a1a1c] hover:bg-white/90 font-bold px-5 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-full"
                data-testid="bulk-apply-button"
              >
                <Sparkles className="w-4 h-4" /> Apply with CV + documents ({selected.size})
              </button>
            </div>
          )}

          {/* Loading skeletons */}
          {loading && results.length === 0 && (
            <div className="space-y-3" aria-live="polite">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 animate-pulse">
                  <div className="flex gap-4">
                    <div className="w-14 h-14 bg-white/10 rounded" />
                    <div className="flex-1">
                      <div className="h-5 bg-white/10 rounded w-2/3 mb-3" />
                      <div className="h-3 bg-white/10 rounded w-1/3 mb-3" />
                      <div className="h-3 bg-white/[0.06] rounded w-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && results.length === 0 && (
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-10 text-center">
              <div className="font-display text-xl mb-2">No matches</div>
              <p className="text-sm text-white/60">Try removing some filters or using a different keyword.</p>
              {activeFilterCount > 0 && (
                <button onClick={clearAll} className="mt-4 px-5 py-2 bg-white text-[#1a1a1c] rounded-full text-sm font-bold">
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-2.5" data-testid="search-results">
              {results.map((j) => {
                const active = selected.has(j.id);
                const initial = (j.company || "?").charAt(0).toUpperCase();
                return (
                  <div
                    key={j.id}
                    onClick={() => toggleSelect(j.id)}
                    className={`cursor-pointer rounded-xl p-4 md:p-5 transition-all flex gap-4 ${
                      active
                        ? "bg-white/[0.08] border border-white shadow-[0_0_0_1px_rgba(255,255,255,0.5)]"
                        : "bg-white/[0.03] border border-white/10 hover:border-white/30 hover:bg-white/[0.05]"
                    }`}
                    data-testid={`job-card-${j.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleSelect(j.id)}
                      className="mt-1 w-5 h-5 accent-white shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {/* Logo placeholder */}
                    <div className="hidden sm:flex w-14 h-14 shrink-0 bg-white/[0.06] border border-white/10 rounded items-center justify-center font-display font-bold text-xl text-white/70">
                      {j.logo ? <img src={j.logo} alt="" className="w-full h-full object-contain rounded" /> : initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <h3 className="font-display font-normal text-lg md:text-xl tracking-tight text-white leading-tight line-clamp-2">{j.title}</h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/60 mt-1.5">
                            {j.posted_at && (
                              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{relTime(j.posted_at)}</span>
                            )}
                            <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{j.company}</span>
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.city}</span>
                          </div>
                        </div>
                        {j.match_score != null && (
                          <div className="text-end">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">Match</div>
                            <div className="num-ticker font-black text-2xl text-white">{j.match_score}</div>
                          </div>
                        )}
                      </div>
                      {j.ai_highlight && (
                        <div className="mt-2 inline-flex items-center gap-2 font-mono text-xs text-white/80">
                          <Sparkles className="w-3 h-3" /> {j.ai_highlight}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2.5 text-xs">
                        <span className="px-2 py-0.5 bg-white/[0.06] border border-white/10 rounded">{j.category}</span>
                        {j.salary_range && <span className="px-2 py-0.5 bg-white/[0.06] border border-white/10 rounded">{j.salary_range}</span>}
                        {(j.tags || []).slice(0, 4).map((tg, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-white/[0.04] border border-white/10 rounded text-white/60">
                            {String(tg).replace(/_/g, " ").toLowerCase()}
                          </span>
                        ))}
                      </div>

                      {/* Description preview + expand */}
                      {j.description && (
                        <div className="mt-3 text-sm text-white/70 leading-relaxed">
                          {expanded.has(j.id) ? (
                            <p className="whitespace-pre-line">{j.description}</p>
                          ) : (
                            <p className="line-clamp-2">{j.description}</p>
                          )}
                        </div>
                      )}

                      {/* Action row */}
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                        {j.description && j.description.length > 120 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(j.id); }}
                            className="font-mono uppercase tracking-widest text-white/55 hover:text-white"
                            data-testid={`job-${j.id}-toggle-details`}
                          >
                            {expanded.has(j.id) ? "Show less ↑" : "Show details ↓"}
                          </button>
                        )}
                        {j.source_url && (
                          <a
                            href={j.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-white hover:text-white/70 ms-auto"
                            data-testid={`job-${j.id}-source`}
                          >
                            <ExternalLink className="w-3 h-3" />
                            View original posting
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Consent Modal */}
      {consentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="consent-modal">
          <div className="bg-[#1a1a1c] w-full max-w-lg border border-white/15 rounded-2xl text-white">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-white" />
                <h3 className="font-display font-bold text-xl">{t("consent_title")}</h3>
              </div>
              <button onClick={() => setConsentOpen(false)} className="p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-white/70 leading-relaxed">{t("consent_body")}</p>

              {/* Sender choice */}
              <div className="space-y-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Send applications via</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => gmailConnected && setSender("gmail")}
                    disabled={!gmailConnected}
                    className={`text-start p-3 rounded-xl border transition-all ${
                      sender === "gmail"
                        ? "bg-white text-[#1a1a1c] border-white"
                        : gmailConnected
                          ? "bg-white/[0.04] border-white/15 hover:border-white/40 text-white"
                          : "bg-white/[0.02] border-white/10 text-white/40 cursor-not-allowed"
                    }`}
                    data-testid="sender-gmail"
                  >
                    <div className="font-bold text-sm flex items-center gap-2">
                      <span className="text-base">✉</span> Your Gmail
                    </div>
                    <div className={`text-xs mt-1 ${sender === "gmail" ? "text-[#1a1a1c]/70" : "text-white/50"}`}>
                      {gmailConnected ? "~99% inbox rate" : "Not connected"}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSender("maqam")}
                    className={`text-start p-3 rounded-xl border transition-all ${
                      sender === "maqam"
                        ? "bg-white text-[#1a1a1c] border-white"
                        : "bg-white/[0.04] border-white/15 hover:border-white/40 text-white"
                    }`}
                    data-testid="sender-maqam"
                  >
                    <div className="font-bold text-sm flex items-center gap-2">
                      <span className="text-base">M</span> Maqam mail
                    </div>
                    <div className={`text-xs mt-1 ${sender === "maqam" ? "text-[#1a1a1c]/70" : "text-white/50"}`}>
                      brief@maqam-ksa.com
                    </div>
                  </button>
                </div>
                {!gmailConnected && (
                  <Link to="/app/profile" className="block text-[11px] text-white/55 hover:text-white underline">
                    Connect Gmail for higher deliverability →
                  </Link>
                )}
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-white/50">Applications to send</span><span className="font-bold num-ticker">{selected.size}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Cover letters</span><span className="font-bold">Auto-tailored per job</span></div>
                <div className="flex justify-between"><span className="text-white/50">Attachments</span><span className="font-bold">CV + all profile documents</span></div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-5 h-5 accent-white" data-testid="consent-checkbox" />
                <span className="text-sm text-white/80">{t("i_consent")}</span>
              </label>
            </div>
            <div className="flex border-t border-white/10">
              <button onClick={() => setConsentOpen(false)} className="flex-1 py-4 font-bold hover:bg-white/5" data-testid="consent-cancel">{t("cancel")}</button>
              <button onClick={doApply} disabled={!consent || applying} className="flex-1 py-4 font-bold bg-white text-[#1a1a1c] hover:bg-white/90 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="consent-confirm">
                {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Sparkles className="w-4 h-4" /> {t("apply_bulk")}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quota modal */}
      {quotaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="quota-modal">
          <div className="bg-[#1a1a1c] w-full max-w-md border border-white/15 rounded-2xl text-white">
            <div className="p-6 border-b border-white/10">
              <Crown className="w-8 h-8 text-white mb-3" />
              <h3 className="font-display font-normal text-2xl tracking-tight mb-1">You've hit your limit</h3>
              <p className="text-white/60 text-sm">Your <span className="font-bold uppercase">{quotaModal.plan}</span> plan includes {quotaModal.total_limit} sends. Upgrade to keep applying.</p>
            </div>
            <div className="p-6 space-y-3">
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex justify-between items-center">
                <div><div className="font-display font-bold">Basic</div><div className="text-xs text-white/50">100 sends / month</div></div>
                <div className="num-ticker font-black text-xl">SAR 449</div>
              </div>
              <div className="bg-white text-[#1a1a1c] border border-white rounded-xl p-4 flex justify-between items-center">
                <div><div className="font-display font-bold">Pro</div><div className="text-xs text-[#1a1a1c]/60">Unlimited</div></div>
                <div className="num-ticker font-black text-xl">SAR 2,600</div>
              </div>
            </div>
            <div className="flex border-t border-white/10">
              <button onClick={() => setQuotaModal(null)} className="flex-1 py-4 font-bold hover:bg-white/5" data-testid="quota-dismiss">Later</button>
              <Link to="/app/billing" className="flex-1 py-4 font-bold bg-white text-[#1a1a1c] hover:bg-white/90 flex items-center justify-center gap-2" data-testid="quota-upgrade">
                Upgrade <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
